import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { scheduledCallService } from "../../services/scheduledCallService";
import { PendingScheduledCallsResponse } from "../../types/pendingScheduledCalls";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { CalendarIcon, ClockIcon, PhoneIcon, UserIcon, BuildingIcon, SearchIcon, FilterIcon } from "lucide-react";
import { LoadingScreen } from "../ui/loading-spinner";
import { ScheduledCallCard } from "./ScheduledCallCard";
import DashboardLayout from "../dashboard/layout/DashboardLayout";

const ScheduledCallsPage: React.FC = () => {
  const [pendingCalls, setPendingCalls] = useState<PendingScheduledCallsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Development mode helpers
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [useMockData, setUseMockData] = useState(() => 
    isDevelopment && (localStorage.getItem('scheduledCalls_useMock') === 'true' || window.location.search.includes('mock=true'))
  );

  // Initialize with today's date
  useEffect(() => {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    setStartDate(todayString);
    setEndDate(todayString);
  }, []);

  const fetchPendingCalls = useCallback(async (start?: string, end?: string) => {
    try {
      console.log('🚀 [Component] Starting to fetch pending calls with dates:', { start, end });
      console.log('🧪 [Component] Using mock data:', useMockData);
      
      setLoading(true);
      setError(null);
      
      // Convert date strings to the required format
      const startDateTime = start ? `${start} 00:00:00` : undefined;
      const endDateTime = end ? `${end} 23:59:59` : undefined;
      
      console.log('📅 [Component] Converted date times:', { startDateTime, endDateTime });
      
      const data = await scheduledCallService.getPendingScheduledCalls(startDateTime, endDateTime);
      console.log('✅ [Component] Successfully fetched scheduled calls:', data);
      setPendingCalls(data);
    } catch (err) {
      console.error("❌ [Component] Error in fetchPendingCalls:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch scheduled calls");
    } finally {
      console.log('🏁 [Component] Setting loading to false');
      setLoading(false);
    }
  }, [useMockData]);

  useEffect(() => {
    if (startDate) {
      fetchPendingCalls(startDate, endDate);
    }
  }, [fetchPendingCalls, startDate, endDate]);

  const handleSearch = useCallback(() => {
    if (startDate && endDate) {
      fetchPendingCalls(startDate, endDate);
    }
  }, [startDate, endDate, fetchPendingCalls]);

  // Get all available organizations from the data
  const availableOrganizations = useMemo(() => {
    if (!pendingCalls?.data) return [];
    return pendingCalls.data.map(org => ({
      id: org.organization.id.toString(),
      name: org.organization.name,
      totalCalls: org.totalPendingCalls
    }));
  }, [pendingCalls]);

  // Filter the data based on selected organizations
  const filteredData = useMemo(() => {
    if (!pendingCalls?.data) return null;
    
    if (selectedOrganizations.length === 0) {
      return pendingCalls;
    }

    const filteredOrganizations = pendingCalls.data.filter(orgData => 
      selectedOrganizations.includes(orgData.organization.id.toString())
    );

    return {
      ...pendingCalls,
      data: filteredOrganizations,
      totalOrganizations: filteredOrganizations.length,
      totalPendingCalls: filteredOrganizations.reduce((sum, org) => sum + org.totalPendingCalls, 0)
    };
  }, [pendingCalls, selectedOrganizations]);

  const handleOrganizationToggle = useCallback((organizationId: string) => {
    setSelectedOrganizations(prev => 
      prev.includes(organizationId)
        ? prev.filter(id => id !== organizationId)
        : [...prev, organizationId]
    );
  }, []);

  const handleSelectAllOrganizations = useCallback(() => {
    if (selectedOrganizations.length === availableOrganizations.length) {
      setSelectedOrganizations([]);
    } else {
      setSelectedOrganizations(availableOrganizations.map(org => org.id));
    }
  }, [selectedOrganizations.length, availableOrganizations]);

  const toggleMockData = () => {
    const newUseMockData = !useMockData;
    setUseMockData(newUseMockData);
    localStorage.setItem('scheduledCalls_useMock', String(newUseMockData));
    
    // Refresh the data with new mode
    if (startDate && endDate) {
      fetchPendingCalls(startDate, endDate);
    }
  };

  const formatDateTime = useCallback((dateString: string, timezone: "IST" | "UTC" = "IST") => {
    const date = new Date(dateString);
    
    if (timezone === "IST") {
      return date.toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      return date.toLocaleString("en-US", {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        activeItem="Scheduled Calls"
        title="Scheduled Calls"
        description="View all pending scheduled calls across organizations"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading scheduled calls...</p>
            <p className="text-xs text-muted-foreground mt-2">This may take a few seconds</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        activeItem="Scheduled Calls"
        title="Scheduled Calls"
        description="View all pending scheduled calls across organizations"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-danger mb-2">Error Loading Scheduled Calls</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button 
              onClick={() => {
                setError(null);
                if (startDate && endDate) {
                  fetchPendingCalls(startDate, endDate);
                }
              }}
              className="flex items-center space-x-2"
            >
              <SearchIcon className="h-4 w-4" />
              <span>Try Again</span>
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!filteredData?.data || filteredData.data.length === 0) {
    const isFiltered = selectedOrganizations.length > 0;
    return (
      <DashboardLayout
        activeItem="Scheduled Calls"
        title="Scheduled Calls"
        description="View all pending scheduled calls across organizations"
      >
        {/* Show date range and filters even when no data */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label htmlFor="startDate" className="block text-sm font-medium mb-2">
                    Start Date
                  </label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="endDate" className="block text-sm font-medium mb-2">
                    End Date
                  </label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading || !startDate || !endDate}
                    className="flex items-center space-x-2"
                  >
                    <SearchIcon className="h-4 w-4" />
                    <span>Search</span>
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center space-x-2"
                  >
                    <FilterIcon className="h-4 w-4" />
                    <span>Filter</span>
                    {selectedOrganizations.length > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {selectedOrganizations.length}
                      </Badge>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {isFiltered ? 'No Matching Scheduled Calls' : 'No Scheduled Calls'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isFiltered 
                  ? 'Try adjusting your organization filters or date range.'
                  : 'There are no pending scheduled calls at the moment.'
                }
              </p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      activeItem="Scheduled Calls"
      title="Scheduled Calls"
      description="View all pending scheduled calls across organizations"
    >
      {isDevelopment && (
        <div className="flex items-center justify-end mb-4">
          <div className="flex items-center space-x-2">
            <Badge variant={useMockData ? "default" : "outline"}>
              {useMockData ? "Mock Data" : "Live API"}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleMockData}
              className="text-xs"
            >
              Switch to {useMockData ? "Live API" : "Mock Data"}
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {/* Date Range Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <label htmlFor="startDate" className="block text-sm font-medium mb-2">
                  Start Date
                </label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label htmlFor="endDate" className="block text-sm font-medium mb-2">
                  End Date
                </label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button 
                onClick={handleSearch} 
                disabled={loading || !startDate || !endDate}
                className="flex items-center space-x-2"
              >
                <SearchIcon className="h-4 w-4" />
                <span>Search</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Organization Filters */}
        {showFilters && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BuildingIcon className="h-5 w-5" />
                <span>Filter by Organization</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSelectAllOrganizations}
                  >
                    {selectedOrganizations.length === availableOrganizations.length 
                      ? 'Deselect All' 
                      : 'Select All'
                    }
                  </Button>
                  {selectedOrganizations.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {selectedOrganizations.length} of {availableOrganizations.length} selected
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {availableOrganizations.map((org) => (
                    <div key={org.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`org-${org.id}`}
                        checked={selectedOrganizations.includes(org.id)}
                        onCheckedChange={() => handleOrganizationToggle(org.id)}
                      />
                      <label 
                        htmlFor={`org-${org.id}`} 
                        className="text-sm cursor-pointer flex-1 flex items-center justify-between"
                      >
                        <span>{org.name}</span>
                        <Badge variant="outline" className="ml-2">
                          {org.totalCalls}
                        </Badge>
                      </label>
                    </div>
                  ))}
                </div>

                {availableOrganizations.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No organizations available to filter
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <BuildingIcon className="h-5 w-5 text-foreground" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Organizations</p>
                  <p className="text-2xl font-bold">{filteredData.totalOrganizations}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-5 w-5 text-good" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Pending Calls</p>
                  <p className="text-2xl font-bold">{filteredData.totalPendingCalls}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-5 w-5 text-mid" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Timeframe</p>
                  <p className="text-sm font-medium">
                    {new Date(filteredData.timeframe.startDate).toLocaleDateString()} -
                    {new Date(filteredData.timeframe.endDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organizations and their pending calls */}
        <div className="space-y-6">
          {filteredData.data.map((orgData) => (
            <Card key={orgData.organization.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BuildingIcon className="h-5 w-5" />
                    <span>{orgData.organization.name}</span>
                  </div>
                  <Badge variant="secondary">
                    {orgData.totalPendingCalls} call{orgData.totalPendingCalls !== 1 ? 's' : ''}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orgData.pendingCalls.map((call) => (
                    <ScheduledCallCard 
                      key={call.id}
                      call={call}
                      formatDateTime={formatDateTime}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ScheduledCallsPage;